import { FormEvent, useContext, useEffect, useState } from 'react'

import { GridContainer, GridItem } from '@entur/grid'
import { Heading3, Label } from '@entur/typography'
import { space } from '@entur/tokens'
import { Tooltip } from '@entur/tooltip'
import { ClosedLockIcon } from '@entur/icons'
import { TextField } from '@entur/form'

import {
  FormField,
  FormFieldTypes,
  formIsValid,
  toObject,
  validateAllFields,
  ValidationFormField,
} from '../../utils/form-utils'
import { EmployeeCard } from './employee-card'
import { trackEvent } from '../../utils/tracking-utils'
import { useEmployeePatch } from '../../hooks/patch-employee'
import { partition } from '../../utils/array-utils'
import { Employee, FetchState } from '../../api-clients/api-types'
import { technicalError } from '../error/error'
import { EmployeePercentageElement } from './employee-percentage-element'
import { useGenerateTicketRights } from '../../hooks/use-ticket-rights'
import { DisabledContext } from '../../contexts/disable-context'
import { EditState } from '../../types'
import { GridSize, infoElementInGridItem } from './employee-info-element'
import {
  disabilityGradeFormField,
  employeeNoFormField,
  employmentPercentageFormField,
  employmentStartDateFormField,
  employmentTypeFormField,
  hireEndCauseFormField,
  hireEndDateFormField,
  hireStartDateFormField,
  leaveFromDateFormField,
  paidPercentageFormField,
  payTypeFormField,
} from '../../utils/common-form-fields'
import { useEmployee } from '../../contexts/employee-context'
import { ValidatableTextField } from '../form/validatable-text-field'
import { companyFormField } from '../form/company-dropdown'
import { EmploymentEndCauseDropdown, EmploymentTypeDropdown, PaymentTypeDropdown } from '../form/dropdowns'
import ValidatableDatePicker from '../form/validatable-date-picker'
import { useClientInfo } from '../../contexts/client-info-context'

type State = {
  company: ValidationFormField<number | undefined>
  employeeNo: ValidationFormField<string>
  employmentType: ValidationFormField<string | undefined>
  hireStartDate: ValidationFormField<Date | undefined>
  employmentPercentage: ValidationFormField<string>
  paidPercentage: ValidationFormField<string>
  disabilityGrade: ValidationFormField<string>
  employmentStartDate: ValidationFormField<Date | undefined>
  payType: ValidationFormField<string | undefined>
  leaveFromDate: ValidationFormField<Date | undefined>
  hireEndCause: ValidationFormField<string | undefined>
  hireEndDate: ValidationFormField<Date | undefined>
}

function initialState(employee: Employee, locked: boolean): State {
  return {
    company: companyFormField(employee, true),
    employeeNo: employeeNoFormField(employee, true),
    employmentType: employmentTypeFormField(employee, true),
    hireStartDate: hireStartDateFormField(employee, true),
    employmentPercentage: employmentPercentageFormField(employee),
    paidPercentage: paidPercentageFormField(employee),
    disabilityGrade: disabilityGradeFormField(employee, !locked),
    employmentStartDate: employmentStartDateFormField(employee, false),
    payType: payTypeFormField(employee, true, !locked),
    leaveFromDate: leaveFromDateFormField(employee, false),
    hireEndCause: hireEndCauseFormField(employee, !locked),
    hireEndDate: hireEndDateFormField(employee, false),
  }
}

export const changedFields = (employee: Employee, state: State) => {
  const fields = Array<FormField<unknown>>()
  if (state.employeeNo && state.employeeNo.value !== employee?.employeeNo) {
    fields.push(state.employeeNo)
  }
  if (state.employmentType && state.employmentType.value !== employee?.employmentType?.code) {
    fields.push(state.employmentType)
  }
  if (state.hireStartDate && state.hireStartDate.value !== employee?.hireStartDate) {
    fields.push(state.hireStartDate)
  }
  if (state.hireEndDate && state.hireEndDate.value !== employee?.hireEndDate) {
    fields.push(state.hireEndDate)
  }
  if (state.hireEndCause && state.hireEndCause.value !== employee?.sensitiveInfo?.hireEndCause?.code) {
    fields.push(state.hireEndCause)
  }
  if (state.payType && state.payType.value !== employee?.sensitiveInfo?.payType?.code) {
    fields.push(state.payType)
  }
  if (state.leaveFromDate && state.leaveFromDate.value !== employee?.leaveFromDate) {
    fields.push(state.leaveFromDate)
  }
  if (state.employmentPercentage && state.employmentPercentage.value !== employee?.employmentPercentage) {
    fields.push(state.employmentPercentage)
  }
  if (
    state.employmentStartDate &&
    state.employmentStartDate.value !== employee?.employmentPercentageDate?.toString()
  ) {
    fields.push(state.employmentStartDate)
  }
  if (state.paidPercentage && state.paidPercentage.value !== employee?.paidPercentage?.toString()) {
    fields.push(state.paidPercentage)
  }
  if (
    state.disabilityGrade &&
    state.disabilityGrade.value !== employee?.sensitiveInfo?.disabilityGrade?.toString()
  ) {
    fields.push(state.disabilityGrade)
  }
  if (state.leaveFromDate && state.leaveFromDate.value !== employee?.leaveFromDate) {
    fields.push(state.leaveFromDate)
  }

  return fields
}

export const EmploymentDataCard = () => {
  const { hasSensitiveAccess } = useClientInfo()
  const { employee } = useEmployee()
  const { disabled } = useContext(DisabledContext)

  const [valid, setValid] = useState(true)
  const [error, setError] = useState<React.ReactNode>()
  const { disable, enable } = useContext(DisabledContext)
  const [editState, setEditState] = useState(EditState.DISPLAY)
  const [state, setState] = useState(initialState(employee, hasSensitiveAccess || false))

  useEffect(() => {
    setState(initialState(employee, hasSensitiveAccess || false))
  }, [employee, hasSensitiveAccess])

  const closeEdit = () => {
    setEditState(EditState.DISPLAY)
    enable()
  }

  const onEdit = (newState: EditState) => {
    setEditState(newState)
    disable()
  }

  const [generateTicketRightsResponse, generateTicketRights] = useGenerateTicketRights(() => {
    closeEdit()
  })

  const [patchStatus, patch] = useEmployeePatch({
    useToast: false,
    onSuccess: () => {
      generateTicketRights()
    },
  })

  const abort = () => {
    setState(initialState(employee, hasSensitiveAccess || false))
    closeEdit()
  }

  const patchData = (changedCompanyData: Array<FormField<unknown>>) => {
    trackEvent('Save-Company-Data', { category: 'Employee' })
    const formElements = changedCompanyData.filter((element) => !element.locked)
    const [normalFields, sensitiveFields] = partition(formElements, (element) => !element.isSensitive)
    if (sensitiveFields.length > 0) {
      patch(toObject(normalFields), toObject(sensitiveFields))
    } else {
      patch(toObject(normalFields))
    }
  }

  const submit = (e: FormEvent) => {
    e.preventDefault()
    const localState = validateAllFields(state)
    const isValid = formIsValid(localState)
    const changedCompanyData = changedFields(employee, state)
    if (isValid && changedCompanyData.length > 0) {
      patchData(changedCompanyData)
    } else if (isValid) {
      generateTicketRights()
    } else {
      setState(localState)
    }
    setValid(isValid)
  }

  // Error handling
  useEffect(() => {
    if (!error && patchStatus === FetchState.ERROR) {
      setError(
        technicalError({
          title: 'Får ikke lagret rettighetseier',
          content: 'En teknisk feil gjør at vi ikke får lagret rettighetshaveren akkurat nå.',
        })
      )
    } else if (!error && generateTicketRightsResponse.status === FetchState.ERROR) {
      setError(
        technicalError({
          title: 'Rettigheter ble ikke oppdatert for rettighetseier',
          content: 'Rettighetseier ble lagret, men rettigheter ble ikke oppdatert. Ta kontakt med Entur support',
        })
      )
    } else if (
      error &&
      patchStatus !== FetchState.ERROR &&
      generateTicketRightsResponse.status !== FetchState.ERROR
    ) {
      setError(undefined)
    }
  }, [generateTicketRightsResponse.status, error, patchStatus])

  const toGridItem = (size: GridSize, testName: string, field?: FormFieldTypes, copyable = false) => {
    return infoElementInGridItem(size, testName, field, copyable)
  }

  const toLockedItem = (field?: FormFieldTypes) => {
    return (
      <Tooltip content="Ikke tilgang til sensitiv data" placement="top">
        <div style={{ marginBottom: space.large }}>
          <Label>{field?.displayName}</Label>
          <span>
            &emsp; <ClosedLockIcon style={{ marginLeft: space.extraSmall }} />
          </span>
        </div>
      </Tooltip>
    )
  }

  return (
    <>
      <Heading3 style={{ marginBottom: space.extraSmall, marginTop: 0 }} className={disabled ? 'disabled' : ''}>
        Ansettelse
      </Heading3>
      <GridContainer spacing="extraSmall">
        <GridItem small={12} medium={4} className="company-card">
          <EmployeeCard
            header="Selskap"
            onSubmit={submit}
            onEdit={() => {
              onEdit(EditState.COMPANY)
            }}
            editMode={editState === EditState.COMPANY}
            onAbort={abort}
            isLoading={
              patchStatus === FetchState.LOADING || generateTicketRightsResponse.status === FetchState.LOADING
            }
            error={error}
            valid={valid}
          >
            <GridItem small={12}>
              <GridContainer spacing="extraSmall">
                {editState === EditState.COMPANY ? (
                  <>
                    <GridItem small={6}>
                      <TextField readOnly value={state.company.displayValue} label={state.company.displayName} />
                    </GridItem>

                    <GridItem small={6}>
                      <ValidatableTextField
                        field={state.employeeNo}
                        update={(e) =>
                          setState({
                            ...state,
                            employeeNo: e,
                          })
                        }
                      />
                    </GridItem>

                    <GridItem small={6} data-cy="employmentType-dropdown">
                      <EmploymentTypeDropdown
                        field={state.employmentType}
                        update={(e) =>
                          setState({
                            ...state,
                            employmentType: e,
                          })
                        }
                      />
                    </GridItem>

                    <GridItem small={6}>
                      <ValidatableDatePicker
                        date={state.hireStartDate}
                        update={(e) =>
                          setState({
                            ...state,
                            hireStartDate: e,
                          })
                        }
                      />
                    </GridItem>
                  </>
                ) : (
                  <>
                    {toGridItem(6, 'companyName', state?.company)}
                    {toGridItem(6, 'employeeNumber', state?.employeeNo, true)}
                    {toGridItem(6, 'employmentType', state?.employmentType)}
                    {toGridItem(6, 'hireStartDate', state?.hireStartDate)}
                  </>
                )}
              </GridContainer>
            </GridItem>
          </EmployeeCard>
        </GridItem>

        <GridItem small={12} medium={8} className="employment-card">
          <EmployeeCard
            header="Stilling"
            onSubmit={submit}
            onEdit={() => {
              onEdit(EditState.EMPLOYMENT)
            }}
            editMode={editState === EditState.EMPLOYMENT}
            onAbort={abort}
            isLoading={
              patchStatus === FetchState.LOADING || generateTicketRightsResponse.status === FetchState.LOADING
            }
            error={error}
            valid={valid}
          >
            {editState === EditState.EMPLOYMENT ? (
              <GridItem small={3}>
                <ValidatableTextField
                  field={state.employmentPercentage}
                  update={(e) =>
                    setState({
                      ...state,
                      employmentPercentage: e,
                    })
                  }
                />
                <ValidatableTextField
                  field={state.paidPercentage}
                  update={(e) =>
                    setState({
                      ...state,
                      paidPercentage: e,
                    })
                  }
                />
                <ValidatableTextField
                  field={state.disabilityGrade}
                  update={(e) =>
                    setState({
                      ...state,
                      disabilityGrade: e,
                    })
                  }
                />
              </GridItem>
            ) : (
              <GridItem small={3}>
                <EmployeePercentageElement field={state.employmentPercentage} />
                <EmployeePercentageElement field={state.paidPercentage} />
                {hasSensitiveAccess === true ? (
                  <EmployeePercentageElement field={state.disabilityGrade} isLast={true} />
                ) : (
                  toLockedItem(state?.disabilityGrade)
                )}
              </GridItem>
            )}

            {editState === EditState.EMPLOYMENT ? (
              <GridItem small={3}>
                <ValidatableDatePicker
                  date={state.employmentStartDate}
                  update={(e) =>
                    setState({
                      ...state,
                      employmentStartDate: e,
                    })
                  }
                />
              </GridItem>
            ) : (
              toGridItem(3, 'employmentStartDate', state?.employmentStartDate)
            )}

            <GridItem small={6}>
              <GridContainer spacing="extraSmall">
                {editState === EditState.EMPLOYMENT ? (
                  <>
                    <GridItem small={6} data-cy="payType-dropdown">
                      <PaymentTypeDropdown
                        field={state.payType}
                        update={(e) =>
                          setState({
                            ...state,
                            payType: e,
                          })
                        }
                      />
                    </GridItem>

                    <GridItem small={6} data-cy="leaveFromDate-picker">
                      <ValidatableDatePicker
                        date={state.leaveFromDate}
                        update={(e) =>
                          setState({
                            ...state,
                            leaveFromDate: e,
                          })
                        }
                      />
                    </GridItem>

                    <GridItem small={6} data-cy="endCause-dropdown">
                      <EmploymentEndCauseDropdown
                        field={state.hireEndCause}
                        update={(e) =>
                          setState({
                            ...state,
                            hireEndCause: e,
                          })
                        }
                      />
                    </GridItem>

                    <GridItem small={6} data-cy="endDate-picker">
                      <ValidatableDatePicker
                        date={state.hireEndDate}
                        update={(e) =>
                          setState({
                            ...state,
                            hireEndDate: e,
                          })
                        }
                      />
                    </GridItem>
                  </>
                ) : (
                  <>
                    {toGridItem(6, 'payType', state.payType)}
                    {toGridItem(6, 'leaveFromDate', state.leaveFromDate)}
                    {toGridItem(6, 'hireEndCause', state.hireEndCause)}
                    {toGridItem(6, 'hireEndDate', state.hireEndDate)}
                  </>
                )}
              </GridContainer>
            </GridItem>
          </EmployeeCard>
        </GridItem>
      </GridContainer>
    </>
  )
}
